import React, { useCallback, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonTitle,
  IonSearchbar,
  IonToolbar,
} from '@ionic/react';
import { AutocompleteSelectItem } from '../../reusable-components/AutocompleteSelect';
import './AppTypeahead.scss';
import { useIntl } from 'react-intl';

interface TypeaheadProps {
  items: AutocompleteSelectItem[];
  title?: string;
  placeholder?: string;
  onItemSelected: (item: AutocompleteSelectItem) => void;
  onClose: () => void;
}

// Adapted from https://ionicframework.com/docs/api/select#typeahead-component
function AppTypeahead(props: TypeaheadProps) {
  const [itemListForDisplay, setItemListForDisplay] = useState<AutocompleteSelectItem[]>([...props.items]);

  const onSearchbarInput = (event: any) => {
    const filteredItems = filterList(event.target.value);
    const newFilteredItems = appendNewTagIfNeeded(filteredItems, event.target.value);
    setItemListForDisplay(newFilteredItems);
  };

  /**
   * Update the rendered view with
   * the provided search query. If no
   * query is provided, all data
   * will be rendered.
   */
  const filterList = (searchQuery: string | null | undefined) => {
    /**
     * If no search query is defined,
     * return all options.
     */
    if (searchQuery === undefined || searchQuery === null) {
      return props.items;
    } else {
      /**
       * Otherwise, normalize the search
       * query and check to see which items
       * contain the search query as a substring.
       */
      const normalizedQuery = searchQuery.toLowerCase();
      return props.items.filter(item => {
        return item.text.toLowerCase().includes(normalizedQuery);
      });
    }
  };

  const appendNewTagIfNeeded = (items: AutocompleteSelectItem[], searchQuery: string | null | undefined) => {
    if (!searchQuery) return items;

    const searchQueryInFilteredList = items.find(item => item.text === searchQuery);
    if (searchQueryInFilteredList) {
      return items;
    }

    //TODO add real key
    // Plus sign will be removed in onItemSelected
    const tagCreationItem = new AutocompleteSelectItem('', `+ ${searchQuery}`, true);
    const localItems = [...items];
    if (localItems.length > 0 && localItems[localItems.length - 1].isNewItem) {
      localItems.pop();
    }

    return [...localItems, tagCreationItem];
  };

  const onItemSelected = useCallback(
    (item: AutocompleteSelectItem) => {
      if (!item.isNewItem) {
        props.onItemSelected(item);
        return;
      }

      // Remove the plus sign from appendNewTagIfNeeded
      const newItem = {
        id: item.id,
        text: item.text.substring(2),
        isNewItem: item.isNewItem,
      };
      props.onItemSelected(newItem);
    },
    [props],
  );

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={props.onClose}>Close</IonButton>
          </IonButtons>
          <IonTitle>{props.title}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          {props.placeholder ? (
            <IonSearchbar placeholder={props.placeholder} onIonInput={onSearchbarInput} />
          ) : (
            <IonSearchbar onIonInput={onSearchbarInput} />
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent color="light" class="ion-padding">
        {itemListForDisplay.map(item =>
          item.isNewItem ? (
            <IonItem key={item.id} className="add-tag-button" onClick={() => onItemSelected(item)}>
              {item.text}
            </IonItem>
          ) : (
            <IonItem key={item.id} onClick={() => onItemSelected(item)}>
              {item.text}
            </IonItem>
          ),
        )}
      </IonContent>
    </>
  );
}

export default AppTypeahead;
